import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { useRenameConversation } from "@/hooks/usePersonalAgent";

interface ConversationTitleProps {
  conversationId: string;
  title: string | null;
}

export default function ConversationTitle({ conversationId, title }: ConversationTitleProps) {
  const renameMutation = useRenameConversation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(title ?? "");
  }, [title]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (!next || next === title) {
      setEditing(false);
      setDraft(title ?? "");
      return;
    }
    renameMutation.mutate(
      { id: conversationId, title: next },
      { onSettled: () => setEditing(false) },
    );
  };

  const cancel = () => {
    setDraft(title ?? "");
    setEditing(false);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        disabled={renameMutation.isPending}
        autoFocus
        sx={{ maxWidth: 600 }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
        {title || "Untitled"}
      </Typography>
      <Tooltip title="Rename">
        <IconButton size="small" onClick={() => setEditing(true)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
